// src/App.js
import { Routes, Route, Navigate } from 'react-router-dom';
import 'antd/dist/reset.css';
import Login from './modules/auth/Login';
import ForcePasswordChange from './modules/auth/ForcePasswordChange';
import Register from './modules/auth/Register';
import DashboardRoutes from './modules/dashboard/routes';
import ModuleDashboardRoutes from './modules/moduleDashboards/routes';
import LeadRoutes from './modules/leads/routes';
import CallSyncRoutes from './modules/callSync/routes';
import WhatsAppRoutes from './modules/whatsApp/routes';
import PrivateRoute from './routes/PrivateRoute';
import PublicRoute from './routes/PublicRoute';
import MainLayout from './components/Layout/MainLayout'; // Adjust path if needed
import BillingRoutes from './modules/billing/routes';
import BranchesRoutes from './modules/branches/routes';
import CustomerRoutes from './modules/customers/routes';
import ProfileViewRoutes from './modules/profile/routes';
import UserRoutes from './modules/users/routes';
import PermissionSetRoutes from './modules/permissionSet/routes';
import SchemeRoutes from './modules/scheme/routes';
import ApprovalRoutes from './modules/approvals/routes';
import SmmRoutes from './modules/smm/routes';
import SystemRoutes from './modules/system/routes';
import PledgeReleaseCaseRoutes from './modules/pledgeReleaseCase/routes';
import WalkInCaseRoutes from './modules/walkInCase/routes';
import GoldInventoryRoutes from './modules/goldInventory/routes';
import SmelterRoutes from './modules/smelter/routes';
import FinancialRoutes from './modules/financials/routes';
import AccountingRoutes from './modules/accounting/routes';
import ManualJournalRoutes from './modules/manualJournal/routes';
import AgentRoutes from './modules/agents/routes';
import TreasuryRoutes from './modules/treasury/routes';
import HrRoutes from './modules/hr/routes';
import GoldVaultRoutes from './modules/goldVault/routes';
import PurityAnalyzerRoutes from './modules/purityAnalyzer/routes';
import SecurityRoutes from './modules/security/routes';
import NotificationRoutes from './modules/notifications/routes';

import PermissionRoute from './routes/PermissionRoute';
import BillVerification from './modules/billing/pages/BillVerification';
import PhoneCapturePage from './pages/PhoneCapturePage';

function App() {
  return (
    <Routes>
      {/* Public bill verification (QR code landing page — no auth) */}
      <Route path="/bill/verify/:id" element={<BillVerification />} />

      {/* Public phone-capture landing page (QR code target — token is the credential, no auth) */}
      <Route path="/capture/:token" element={<PhoneCapturePage />} />
      <Route
        path="/system/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'system', action: 'view' }]}>
                <SystemRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />
      <Route path="/" element={<Navigate to="/login" />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route path="/register" element={<Register />} />

      {/* Forced password rotation (PASSWORD_EXPIRY login policy). Rendered
          WITHOUT MainLayout on purpose: the user is signed in but the backend
          rejects every other call, so there is nothing else to navigate to. */}
      <Route
        path="/change-password"
        element={
          <PrivateRoute>
            <ForcePasswordChange />
          </PrivateRoute>
        }
      />
      <Route path="billing/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'billing', action: 'view' }]}>
                <BillingRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        } />

      <Route path="branches/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'branches', action: 'view' }]}>
                <BranchesRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        } />

      <Route
        path="/dashboard/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'dashboard', action: 'view' }]}>
                <DashboardRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/module-dashboards/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'dashboard', action: 'view' }]}>
                <ModuleDashboardRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/leads/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <LeadRoutes />
            </MainLayout>
          </PrivateRoute>
        }
      />

      {/* The inbox is not permission-gated: every user has one, and it is
          scoped to their own JWT server-side. */}
      <Route
        path="/notifications/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <NotificationRoutes />
            </MainLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/calling/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <CallSyncRoutes />
            </MainLayout>
          </PrivateRoute>
        }
      />

      {/* WhatsApp inbox + admin settings. Access is enforced server-side; the
          config routes require an admin. */}
      <Route
        path="/whatsapp/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <WhatsAppRoutes />
            </MainLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/profile/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <ProfileViewRoutes />
            </MainLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/users/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'user', action: 'view' }]}>
                <UserRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/permission-sets/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'permission', action: 'view' }]}>
                <PermissionSetRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/security/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'security', action: 'view' }]}>
                <SecurityRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/customers/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'customer', action: 'view' }]}>
                <CustomerRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/scheme/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'scheme', action: 'view' }]}>
                <SchemeRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/approvals/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'approval', action: 'view' }]}>
                <ApprovalRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/smm/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'smm', action: 'view' }]}>
                <SmmRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/pledge-release-cases/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'pledgeReleaseCase', action: 'view' }]}>
                <PledgeReleaseCaseRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/walk-in-cases/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'walkInCase', action: 'view' }]}>
                <WalkInCaseRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/gold-inventory/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'goldInventory', action: 'view' }]}>
                <GoldInventoryRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/smelters/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'smelter', action: 'view' }]}>
                <SmelterRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/financials/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'financials', action: 'view' }]}>
                <FinancialRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/accounting/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'accounting', action: 'view' }]}>
                <AccountingRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/manual-journal/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'accounting', action: 'view' }]}>
                <ManualJournalRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/agents/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'agents', action: 'view' }]}>
                <AgentRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/treasury/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'treasury', action: 'view' }]}>
                <TreasuryRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/hr/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'hr', action: 'view' }]}>
                <HrRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/vault/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute requiredPermissions={[{ module: 'inventoryBranchVault', action: 'view' }]}>
                <GoldVaultRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/purity/*"
        element={
          <PrivateRoute>
            <MainLayout>
              <PermissionRoute anyOf={[
                { module: 'purityAnalyzer', action: 'view' },
                { module: 'purityDashboard', action: 'view' },
                { module: 'purityAssay', action: 'view' },
                { module: 'purityTests', action: 'view' },
                { module: 'purityCalibrate', action: 'view' },
                { module: 'purityCalibrations', action: 'view' },
                { module: 'purityXrf', action: 'view' },
                { module: 'purityVersions', action: 'view' },
                { module: 'purityAnalytics', action: 'view' },
                { module: 'purityMachines', action: 'view' },
                { module: 'puritySettings', action: 'view' },
              ]}>
                <PurityAnalyzerRoutes />
              </PermissionRoute>
            </MainLayout>
          </PrivateRoute>
        }
      />

    </Routes >
  );
}

export default App;
